import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Phone, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f9e37d31-967b-4854-b567-b2784bb2ed6c/artifacts/ya50dq4o_%D7%9C%D7%95%D7%92%D7%95%20%D7%A9%D7%97%D7%95%D7%A8.jpg";
const OWNER_PHOTO_URL = "https://customer-assets.emergentagent.com/job_f9e37d31-967b-4854-b567-b2784bb2ed6c/artifacts/eq3ji2rt_WhatsApp%20Image%202025-02-18%20at%2013.24.43.jpeg";

// Format number with commas
const formatNumber = (num) => {
  if (!num && num !== 0) return "0";
  return Math.round(Number(num)).toLocaleString("he-IL");
};

const ResultsPage = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);

  useEffect(() => {
    // Get results from localStorage (PII kept out of URL for security)
    const currentPayment = localStorage.getItem("momento_current");
    const newPayment = localStorage.getItem("momento_new");
    const monthlySavings = localStorage.getItem("momento_savings");
    const fv5 = localStorage.getItem("momento_fv5");
    const fv10 = localStorage.getItem("momento_fv10");
    const fv20 = localStorage.getItem("momento_fv20");
    const name = localStorage.getItem("momento_name");
    const phone = localStorage.getItem("momento_phone");

    if (!currentPayment || !newPayment) {
      // No data, redirect to calculator
      navigate("/");
      return;
    }

    setResults({
      currentPayment: Number(currentPayment),
      newPayment: Number(newPayment),
      monthlySavings: Number(monthlySavings),
      fv5: Number(fv5),
      fv10: Number(fv10),
      fv20: Number(fv20),
      name: name || "",
      phone: phone || "",
    });

    // Track Lead event for Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'Lead');
    }

    // Push to dataLayer for GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'lead_conversion',
        lead_value: monthlySavings,
      });
    }
  }, [navigate]);

  if (!results) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#C5A45A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[#F8FAFC]"
      data-testid="results-page"
    >
      {/* Header */}
      <header className="bg-[#0F172A] py-3 sm:py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <img src={LOGO_URL} alt="MOMENTO" className="h-10 sm:h-12 object-contain" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
        {/* Title */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-3">
            <CheckCircle2 className="w-12 h-12 sm:w-16 sm:h-16 text-[#10B981]" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0F172A] mb-2">
            מהישרדות לצמיחה
          </h1>
          <p className="text-sm sm:text-base text-[#64748B]">
            התוכנית הכלכלית החדשה של המשפחה שלך
          </p>
        </div>

        {/* Savings Comparison */}
        <Card className="mb-4 sm:mb-6 border-0 shadow-lg overflow-hidden">
          <CardContent className="p-0">
            <div className="grid md:grid-cols-2">
              {/* Current */}
              <div className="bg-[#FEF2F2] p-4 sm:p-6">
                <p className="text-sm sm:text-base text-[#991B1B] font-medium mb-2">המצב היום</p>
                <p className="text-3xl sm:text-4xl font-bold text-[#DC2626]">
                  {formatNumber(results.currentPayment)} ₪
                </p>
                <p className="text-xs sm:text-sm text-[#B91C1C] mt-1">תשלום חודשי לבנקים</p>
              </div>
              
              {/* New */}
              <div className="bg-[#ECFDF5] p-4 sm:p-6">
                <p className="text-sm sm:text-base text-[#065F46] font-medium mb-2">המצב החדש</p>
                <p className="text-3xl sm:text-4xl font-bold text-[#10B981]">
                  {formatNumber(results.newPayment)} ₪
                </p>
                <p className="text-xs sm:text-sm text-[#047857] mt-1">תשלום חודשי חדש</p>
              </div>
            </div>

            {/* Savings Banner */}
            <div className="gold-gradient p-4 sm:p-6 text-center">
              <p className="text-white text-base sm:text-lg mb-1">חיסכון חודשי</p>
              <p className="text-4xl sm:text-5xl md:text-6xl font-bold text-white">
                {formatNumber(results.monthlySavings)} ₪
              </p>
              <p className="text-white/80 text-xs sm:text-sm mt-2">יישארו בכיס שלכם כל חודש!</p>
            </div>
          </CardContent>
        </Card>

        {/* Future Value */}
        {/* Video Testimonials */}
        <Card className="mb-4 sm:mb-6 border-0 shadow-lg">
          <CardContent className="p-4 sm:p-6">
            <div className="text-center mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-[#0F172A] mb-1">
                מה אומרים לקוחות מרוצים?
              </h3>
              <p className="text-xs sm:text-sm text-[#64748B]">
                עדויות אמת מאלפי משפחות שיצאו מהבוץ הכלכלי
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3">
              {[
                { id: "RRiVV6PZxVs", label: "עדות #1" },
                { id: "xhDUAkYIQmA", label: "עדות #2" },
                { id: "gucvcPy5Ok8", label: "ראיון עם אלירן בן דוד" },
              ].map((video) => (
                <div key={video.id} className="space-y-2">
                  <div 
                    className="relative w-full mx-auto rounded-xl overflow-hidden shadow-md bg-black" 
                    style={{ aspectRatio: '9/16', maxWidth: '250px' }}
                  >
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src={`https://www.youtube.com/embed/${video.id}?rel=0&modestbranding=1`}
                      title={video.label}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                  <p className="text-center text-xs sm:text-sm font-medium text-[#475569]">
                    {video.label}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Contact Confirmation */}
        <Card className="mb-4 sm:mb-6 border-0 shadow-lg bg-gradient-to-br from-[#0F172A] to-[#1E293B]">
          <CardContent className="p-5 sm:p-6 text-center">
            <CheckCircle2 className="w-10 h-10 sm:w-12 sm:h-12 text-[#C5A45A] mx-auto mb-3" />
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
              נחזור אליכם בהקדם!
            </h3>
            <p className="text-sm sm:text-base text-white/80">
              מומחה מומנטו יצור איתכם קשר תוך 24 שעות לתכנון אישי ובחינת ההיתכנות
            </p>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <p className="text-xs text-[#94A3B8] text-center mt-6 leading-relaxed px-2">
          הנתונים המוצגים הם להמחשה בלבד ומתבססים על הנחות ריבית ותשואה ממוצעות בשוק ההון. 
          אישור ההלוואה כפוף לחיתום בנקאי, שמאות ורגולציה. 
          אין במידע זה משום ייעוץ השקעות או התחייבות לתשואה.
        </p>
      </div>
    </motion.div>
  );
};

export default ResultsPage;
